import * as formik from "formik";
import { useContext, useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import AccountService from "../../services/AccountService";
import { AlertType } from "../../types/AlertMessage";
import { CurrentUser } from "../../types/User";
import { useAlertContext } from "../alert/AlertContext";
import { AuthContext } from "../auth/AuthContext";

export const Login = () => {
  const navigate = useNavigate();
  const alertContext = useAlertContext();
  const authContext = useContext(AuthContext);
  const accountService = new AccountService(alertContext.showAlert);
  const [badCredentials, setBadCredentials] = useState(false);
  const [badToken, setBadToken] = useState(false);
  const [loadingToken, setLoadingToken] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);

  const { Formik } = formik;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    let refreshToken = urlParams.get("refreshToken");
    if (token && refreshToken) {
      setLoadingToken(true);
      refreshToken = atob(refreshToken);
      accountService
        .LoginSso(token, refreshToken)
        .then((res) => {
          if (!res) {
            setBadToken(true);
            setLoadingToken(false);
            return;
          } else {
            let userDetails: CurrentUser = {
              username: res.user.username,
              id: res.user.id,
              firstName: res.user.firstName,
              lastName: res.user.lastName,
              roles: res.user.roles.map((r: any) => r.name),
              token: token,
              tokenExpiry: res.tokenExpiry,
              refreshToken: res.refreshToken
            };

            authContext?.login(userDetails);

            navigate("/admin");
            return;
          }
        })
        .catch(() => {
          setBadToken(true);
          setLoadingToken(false);
        });

      // Remove the token from the query params
      urlParams.delete("token");
    }

    if (authContext?.user) {
      navigate("/admin");
    }
  }, []);

  const schema = yup.object({
    Username: yup.string().required(),
    Password: yup.string().required(),
  });

  const handleLoginSubmit = (values: any) => {
    setLoadingLogin(true);
    accountService
      .Login(values.Username, values.Password)
      .then((r) => {
        if (r === undefined) {
          setBadCredentials(true);
          setLoadingLogin(false);
        } else {
          setBadCredentials(false);
          authContext?.login({
            username: r.user.username,
            firstName: r.user.firstName,
            lastName: r.user.lastName,
            id: r.user.id,
            token: r.token,
            roles: r.user.roles.map((r) => r.name),
            tokenExpiry: r.tokenExpiry,
            refreshToken: r.refreshToken,
          });

          alertContext.showAlert({
            message: "Login successful",
            type: AlertType.Information,
          });

          navigate("/admin");
        }
      })
      .catch((e) => {
        alertContext.showAlert({ message: e.message, type: AlertType.Error });
        setLoadingLogin(false);
      });
  };

  return (
    <>
      <Formik
        validationSchema={schema}
        onSubmit={handleLoginSubmit}
        initialValues={{ Username: "", Password: "" }}
      >
        {({
          handleChange,
          values,
          errors,
          handleBlur,
          touched,
          handleSubmit,
        }) => (
          <Form
            onSubmit={handleSubmit}
            style={{
              display: "block",
              position: "initial",
              backgroundColor: "#f78f1e",
            }}
            className="modal show"
          >
            <Modal.Dialog>
              <Modal.Header>
                <Modal.Title>Login</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {!loadingToken && (
                  <>
                    {badToken && (
                      <p className="text text-danger">
                        Unable to login automatically from SSO. Please try
                        manually below.
                      </p>
                    )}
                    <Form.Group className="mb-3">
                      <Form.Label>Username</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter username"
                        onChange={handleChange}
                        value={values.Username}
                        isInvalid={touched.Username && !!errors.Username}
                        onBlur={handleBlur}
                        name="Username"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.Username}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Enter password"
                        onChange={handleChange}
                        value={values.Password}
                        isInvalid={touched.Password && !!errors.Password}
                        onBlur={handleBlur}
                        name="Password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.Password}
                      </Form.Control.Feedback>
                    </Form.Group>
                    {badCredentials && (
                      <>
                        <Form.Text className="text-danger">
                          Bad username or password
                        </Form.Text>
                        <br />
                      </>
                    )}
                  </>
                )}
                {loadingToken && (
                  <>
                    <p>Logging you into PlusBus from SSO...</p>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" type="submit" disabled={loadingLogin}>
                  Login
                </Button>
                <Button
                  variant="danger"
                  onClick={() => {
                    navigate("/admin/forgottenPassword");
                  }}
                >
                  Forgotten Password
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Form>
        )}
      </Formik>
    </>
  );
};
